import {
    FGButton,
    FGIcons,
    FGDropdownMenu,
    FGDropdownMenuListContent,
    FGDropdownMenuListItem,
    FGDropdownMenuDivider,
} from '@fg/design-system';
import { get } from 'lodash/fp';
import React from 'react';
import { InsightWithDateString } from './InsightItem';

export type InsightMenuProps = {
    insight: InsightWithDateString;
    canShare?: boolean;
    canDownloadData?: boolean;
    onEdit?: (insight: InsightWithDateString) => void;
    onBroadcast?: (insight: InsightWithDateString, sendEmail: boolean) => void;
    onDownload?: (insight: InsightWithDateString) => void;
    onDelete?: (insight: InsightWithDateString) => void;
    isGQInternalUser?: boolean;
};

const InsightMenu = React.memo(
    ({
        insight,
        canShare,
        canDownloadData,
        onEdit,
        onBroadcast,
        onDownload,
        onDelete,
    }: InsightMenuProps) => {
        const dailyURL = get('dailyEmailTemplateURL', insight) as
            | string
            | undefined;
        const weeklyURL = get('weeklyEmailTemplateURL', insight) as
            | string
            | undefined;
        const pdfURL = get('pdfURL', insight) as string | undefined;

        return (
            <FGDropdownMenu
                trigger="click"
                appendTo={() => document.body}
                triggerElement={
                    <FGButton
                        variant="ghost"
                        icon={<FGIcons icon="MenuDotsVertical" />}
                        aria-label="Insight actions"
                        data-testid="insight-actions"
                        title="Insight actions"
                        style={{ height: 24, width: 24 }}
                    />
                }>
                <FGDropdownMenuListContent className="insight-menu-list">
                    <FGDropdownMenuListItem>
                        <FGButton
                            variant="ghost"
                            fullWidth
                            icon={<FGIcons icon="Edit" />}
                            data-testid="insight-edit"
                            disabled={!insight.canEdit || !onEdit}
                            onClick={() => onEdit?.(insight)}>
                            Edit
                        </FGButton>
                    </FGDropdownMenuListItem>

                    {canShare && (
                        <FGDropdownMenuListItem>
                            <FGButton
                                variant="ghost"
                                fullWidth
                                icon={
                                    <FGIcons
                                        icon={insight.shared ? 'EyeBan' : 'Eye'}
                                    />
                                }
                                disabled={!insight.createdByMe || !onBroadcast}
                                data-testid="insight-publish-toggle"
                                onClick={() => onBroadcast?.(insight, false)}>
                                {insight.shared ? 'Unpublish' : 'Publish'}
                            </FGButton>
                        </FGDropdownMenuListItem>
                    )}

                    {canShare && !insight.shared && (
                        <FGDropdownMenuListItem>
                            <FGButton
                                variant="ghost"
                                fullWidth
                                icon={<FGIcons icon="Mail" />}
                                disabled>
                                Email
                            </FGButton>
                        </FGDropdownMenuListItem>
                    )}

                    {canDownloadData && (
                        <FGDropdownMenuListItem>
                            <FGButton
                                variant="ghost"
                                fullWidth
                                icon={<FGIcons icon="Download" />}
                                data-testid="insight-download"
                                disabled={!onDownload}
                                onClick={() => onDownload?.(insight)}>
                                Download
                            </FGButton>
                        </FGDropdownMenuListItem>
                    )}

                    <FGDropdownMenuDivider />

                    <FGDropdownMenuListItem>
                        <FGButton
                            variant="ghost"
                            fullWidth
                            icon={<FGIcons icon="Share" />}
                            disabled={!dailyURL}
                            onClick={() =>
                                dailyURL && window.open(dailyURL, '_blank')
                            }>
                            Open as Daily
                        </FGButton>
                    </FGDropdownMenuListItem>

                    <FGDropdownMenuListItem>
                        <FGButton
                            variant="ghost"
                            fullWidth
                            icon={<FGIcons icon="Share" />}
                            disabled={!weeklyURL}
                            onClick={() =>
                                weeklyURL && window.open(weeklyURL, '_blank')
                            }>
                            Open as Weekly
                        </FGButton>
                    </FGDropdownMenuListItem>

                    <FGDropdownMenuListItem>
                        <FGButton
                            variant="ghost"
                            fullWidth
                            icon={<FGIcons icon="Document" />}
                            data-testid="insight-export-pdf"
                            disabled={!pdfURL}
                            onClick={() =>
                                pdfURL && window.open(pdfURL, '_blank')
                            }>
                            Export PDF
                        </FGButton>
                    </FGDropdownMenuListItem>

                    <FGDropdownMenuDivider />

                    <FGDropdownMenuListItem>
                        <FGButton
                            variant="ghost"
                            fullWidth
                            icon={<FGIcons icon="TrashBin" />}
                            data-testid="insight-delete"
                            disabled={!insight.createdByMe || !onDelete}
                            onClick={() => onDelete?.(insight)}>
                            Delete
                        </FGButton>
                    </FGDropdownMenuListItem>
                </FGDropdownMenuListContent>
            </FGDropdownMenu>
        );
    }
);

export default InsightMenu;
